/**
 * Shared nationality/country code resolution utilities for Officer Notification forms.
 * Converts SSM basecodes (B15xxx), ISO codes (MY/MYS), and demonyms (MALAYSIAN) to dropdown values (MALAYSIA).
 */

const SSM_AND_ISO_CODE_MAP: Record<string, string> = {
  // ISO 2-letter codes
  MY: 'MALAYSIA', SG: 'SINGAPORE', ID: 'INDONESIA', CN: 'CHINA',
  IN: 'INDIA', TH: 'THAILAND', PH: 'PHILIPPINES', VN: 'VIETNAM',
  JP: 'JAPAN', KR: 'KOREA_SOUTH', TW: 'TAIWAN', HK: 'HONG_KONG',
  GB: 'UNITED_KINGDOM', US: 'UNITED_STATES_OF_AMERICA',
  AU: 'AUSTRALIA', NZ: 'NEW_ZEALAND', BD: 'BANGLADESH', PK: 'PAKISTAN',
  MM: 'MYANMAR', KH: 'CAMBODIA', BN: 'BRUNEI', LA: 'LAOS',
  LK: 'SRI_LANKA',
  // ISO 3-letter codes
  MYS: 'MALAYSIA', SGP: 'SINGAPORE', IDN: 'INDONESIA', CHN: 'CHINA',
  IND: 'INDIA', THA: 'THAILAND', PHL: 'PHILIPPINES', VNM: 'VIETNAM',
  JPN: 'JAPAN', KOR: 'KOREA_SOUTH', TWN: 'TAIWAN', HKG: 'HONG_KONG',
  GBR: 'UNITED_KINGDOM', USA: 'UNITED_STATES_OF_AMERICA',
  AUS: 'AUSTRALIA', NZL: 'NEW_ZEALAND', BGD: 'BANGLADESH', PAK: 'PAKISTAN',
  MMR: 'MYANMAR', KHM: 'CAMBODIA', BRN: 'BRUNEI', LAO: 'LAOS',
  LKA: 'SRI_LANKA',
  // SSM system basecodes (B15xxx series)
  B15101: 'MALAYSIA', B15135: 'MALAYSIA', B15201: 'SINGAPORE',
  B15301: 'BRUNEI', B15401: 'INDONESIA', B15501: 'THAILAND',
  B15601: 'PHILIPPINES', B15701: 'VIETNAM', B15801: 'MYANMAR',
  B15901: 'CAMBODIA', B16001: 'LAOS', B16101: 'CHINA',
  B16201: 'HONG_KONG', B16301: 'TAIWAN', B16401: 'JAPAN',
  B16501: 'KOREA_SOUTH', B16601: 'INDIA', B16701: 'PAKISTAN',
  B16801: 'BANGLADESH', B16901: 'SRI_LANKA',
  B19901: 'UNITED_KINGDOM', B20001: 'UNITED_STATES_OF_AMERICA',
  B20101: 'AUSTRALIA', B20201: 'NEW_ZEALAND',
};

/**
 * Resolve a nationality display value (code, demonym, or name) to a dropdown-compatible value.
 * Examples: "B15135" → "MALAYSIA", "MALAYSIAN" → "MALAYSIA", "MALAYSIA" → "MALAYSIA"
 */
export function resolveNationalityValue(
  raw: string,
  nationalities: { value: string; label: string }[],
): string {
  if (!raw) return '';
  const upper = raw.trim().toUpperCase();

  // 1. Exact match against dropdown values/labels
  const exact = nationalities.find(n => n.value === upper || n.label === upper);
  if (exact) return exact.value;

  // 2. SSM basecode / ISO code lookup
  const mapped = SSM_AND_ISO_CODE_MAP[upper];
  if (mapped) return mapped;

  // 3. Strip common demonym suffixes → country stem, then match
  const stem = upper
    .replace(/(IAN|AN|ESE|ISH|I|ER)$/i, '')
    .replace(/\s+$/, '');
  if (stem) {
    const byStem = nationalities.find(n =>
      n.value.startsWith(stem) || n.label.startsWith(stem),
    );
    if (byStem) return byStem.value;
  }

  // 4. Fallback: return raw value as-is
  return upper;
}
